import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ADMIN_PAGES_KEYS, NS } from '../../../i18n/constants/keys';
import { toast } from '../../../ui/toast';
import { DIALOG_TYPE } from '../../../constants';
import { AddCircleIcons, RefreshListIcons } from '../../../icons/icons';
import {
  Button,
  ContextMenu,
  DataTable,
  Dialog,
  FilterSearch,
  MainLayout,
} from '../../../ui';

import { Modal } from '../../../ui/modal/modal';
import {
  deleteEmployee,
  getEmployee,
} from '../../../utils/api_service/employees';
import { Permission } from '../../../utils/helpers/permissions';
import PermissionGate from '../../../utils/hooks/PermissionGate';
import { fetchOrganizationUsers } from '../../../utils/redux_store/features/organization_user_reducer';
import { store } from '../../../utils/redux_store/store';
import { EmployeeUser } from '../../../utils/types/admin_types';
import baseStyles from '../admin.module.css';
import { getColumns } from './Functions';
import { getMenuItemsForMenu } from './Functions/GetContextMenuItems';
import { useEmployeeManagement } from './Hooks';
import { BulkUploadModal } from './Modals/BulkUploadModal';
import { CreateEmployeeModal } from './Modals/CreateEmployeeModal';
import { EditEmployeeModal } from './Modals/EditEmployeeModal';

export const EmployeeManagement = () => {
  const { t } = useTranslation(NS.adminPages);
  const {
    employeeUserList,
    loading,
    pageNumber,
    totalPages,
    userGroup,
    editedEmployee,
    editModalVisible,
    setEditModalVisible,
    inviteModalVisible,
    setInviteModalVisible,
    inviteEmployee,
    inviteBulkEmployees,
    bulkUploadModalVisible,
    setBulkUploadModalVisible,
    jobTitleList,
    onAddNewJobTitle,
    organizationUnitList,
    onPageChange,
    editUser,
    userRole,
    handleSearchTermChange,
    singleEmployee,
    setSingleEmployee,
    setEditedEmployee,
    setEmployeeUserList,
    totalRecords,
  } = useEmployeeManagement();

  // Context menu state
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedRow, setSelectedRow] = useState<EmployeeUser | null>(null);
  const [deleteConfirmState, setDeleteConfirmState] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] =
    useState<EmployeeUser | null>(null);
  const handleDeleteEmployee = async () => {
    if (!employeeToDelete) return;
    try {
      const response = await deleteEmployee(employeeToDelete.id);
      if (response.ok) {
        const updatedEmployees = employeeUserList.filter(
          (employee) => employee.id !== employeeToDelete.id,
        );
        setEmployeeUserList(updatedEmployees);
        toast.success('Employee deleted.', { position: 'bottom-center' });
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error deleting employee: ' + error, {
        position: 'bottom-center',
      });
    } finally {
      setDeleteConfirmState(false);
      setEmployeeToDelete(null);
    }
  };
  // Open context menu for row
  const handleOpenMenu = (
    e: React.MouseEvent<HTMLButtonElement>,
    row: EmployeeUser,
  ) => {
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  // Close context menu
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };
  const openEditModal = async (employee: EmployeeUser) => {
    const response = await getEmployee(employee.id);
    setSingleEmployee(response);
    setEditedEmployee(employee);
    setEditModalVisible(true);
  };
  const openDeleteDialog = (employee: EmployeeUser) => {
    setEmployeeToDelete(employee);
    setDeleteConfirmState(true);
  };
  // Menu items with permissions
  const contextMenuItems = getMenuItemsForMenu({
    onEdit: (row: EmployeeUser) => {
      openEditModal(row); // <-- call the function with the row!
    },
    onDelete: (row: EmployeeUser) => {
      openDeleteDialog(row); // <-- open the confirm dialog
    },
    t,
  });

  return (
    <div className='w-full h-full flex flex-col gap-5'>
      { /* Breadcrumb */ }
      <nav
          className='flex w-full justify-between items-center border-b pb-3 text-sm'
          aria-label='Breadcrumb'
      >
        <ol className='inline-flex items-center space-x-1 md:space-x-2'>
          <li>
            <Link to={'#'} className='text-primary-textGray hover:underline'>
              {t(ADMIN_PAGES_KEYS.breadcrumbs.settings)}
            </Link>
          </li>
          <li>
            <span className='text-gray-400'>/</span>
          </li>
          <li>
            <Link
                to={'#'}
                className='text-primary-main font-semibold hover:underline'
            >
              {t(ADMIN_PAGES_KEYS.employeeManagement.title)}
            </Link>
          </li>
        </ol>
      </nav>
      <MainLayout>
        <div className='flex w-full flex-col lg:flex-row justify-start items-center gap-5'>
          <div className='flex w-full max-w-md'>
            <FilterSearch
                onSearch={handleSearchTermChange}
                searchText={t(ADMIN_PAGES_KEYS.employeeManagement.searchPlaceholder)}
            />
          </div>
          <div className='flex w-full gap-2'>
            <Button
                label={t(ADMIN_PAGES_KEYS.employeeManagement.refresh)}
                icon={<RefreshListIcons />}
                onClick={() => store.dispatch(fetchOrganizationUsers())}
                variant='secondary'
                hasIcon
            />
            <Button
                icon={<AddCircleIcons />}
                onClick={() => setBulkUploadModalVisible(true)}
                variant='secondary'
                label={t(ADMIN_PAGES_KEYS.employeeManagement.inviteBulk)}
                hasIcon
            />
            <PermissionGate requiredPermissions={[Permission.USERS_CREATE]}>
              <Button
                  icon={<AddCircleIcons />}
                  onClick={() => setInviteModalVisible(true)}
                  variant='primary'
                  label={t(ADMIN_PAGES_KEYS.employeeManagement.inviteUsers)}
                  hasIcon
              />
            </PermissionGate>
          </div>
        </div>
        <div className='relative w-full h-[calc(100dvh-260px)] min-h-[360px] min-w-0 sm:h-[calc(100vh-230px)]'>
          <DataTable<EmployeeUser>
              isLoading={loading}
              columns={getColumns({
                handleOpenMenu,
                handleCloseMenu,
                anchorEl,
                t,
              })}
              data={employeeUserList}
              rowsPerPage={10}
              onPageChange={onPageChange}
              currentPage={pageNumber}
              totalPages={totalPages}
              totalRecords={totalRecords}
          />
        </div>
      </MainLayout>

      <Dialog
          title='Are you sure you want to delete this employee?'
          handleApply={handleDeleteEmployee}
          confirmButtonLable='Yes, Cancel'
          isOpen={deleteConfirmState}
          onClose={() => setDeleteConfirmState(false)}
          cancelButtonLable='No, Delete'
          dialogType={DIALOG_TYPE.ERROR}
          justify='end'
      >
        <p className='text-primary-textGray text-sm'>
          This action cannot be undone, changes that have you made will be lost.
        </p>
      </Dialog>
      <Modal
          visible={inviteModalVisible}
          modalContent={
            <CreateEmployeeModal
                employeeUsers={employeeUserList}
                organizationUnits={organizationUnitList}
                jobTitles={jobTitleList}
                addNewJobTitle={onAddNewJobTitle}
                createEmployee={inviteEmployee}
            />
          }
          toggle={() => setInviteModalVisible(false)}
          headerText={t(ADMIN_PAGES_KEYS.employeeManagement.inviteModal.title)}
      />
      <Modal
          visible={editModalVisible}
          modalContent={
            <EditEmployeeModal
                employeeUsers={employeeUserList}
                organizationUnits={organizationUnitList}
                jobTitles={jobTitleList}
                userGroupList={userGroup}
                userRoleList={userRole}
                singleEmployee={singleEmployee}
                employee={editedEmployee || ({} as EmployeeUser)}
                addNewJobTitle={onAddNewJobTitle}
                editUser={editUser}
            />
          }
          toggle={() => setEditModalVisible(false)}
          headerText='Edit Employee Details'
          headerClass={baseStyles.modalHeader}
      />
      <Modal
          visible={bulkUploadModalVisible}
          modalContent={
            <BulkUploadModal inviteEmployeesFromCsv={inviteBulkEmployees} />
          }
          toggle={() => setBulkUploadModalVisible(false)}
          headerText='Bulk Invite Employees'
          headerClass={baseStyles.modalHeader}
      />
      { /* Context Menu */ }
      { anchorEl && selectedRow && (
        <>
          <div
              className='fixed inset-0 z-40'
              onClick={handleCloseMenu}
              aria-label='Close context menu'
          />
          <ContextMenu<EmployeeUser>
              anchorEl={anchorEl}
              onClose={handleCloseMenu}
              row={selectedRow}
              menuItems={contextMenuItems}
          />
        </>
      ) }
    </div>
  );
};
